import { useEffect, useRef } from "react";
import type { UseFormWatch, UseFormSetValue } from "react-hook-form";
import type { IntakeFormQuestion } from "../requests/vendor-intake-form-response";

interface ConditionalRequiredParams {
  watch: UseFormWatch<{ questions: IntakeFormQuestion[] }>;
  setValue: UseFormSetValue<{ questions: IntakeFormQuestion[] }>;
}

interface ChildDep {
  childIndex: number;
  triggerValues: string[];
  originalRequired: boolean;
}

/**
 * Watches parent question responses and toggles child `required` accordingly.
 *
 * When a parent's response matches one of the child's `parent_trigger_values`,
 * the child becomes required. Otherwise it reverts to its original `required` value.
 *
 * The watch callback fires with `name === undefined` when `reset()` is called,
 * which is used to rebuild the dependency map after async question loading.
 */
export function useConditionalRequired({
  watch,
  setValue,
}: ConditionalRequiredParams) {
  const depsRef = useRef<Map<string, ChildDep[]>>(new Map());

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      const questions = value.questions;
      if (!questions || questions.length === 0) return;

      // name === undefined means reset() was called — rebuild dep map + run initial evaluation
      if (name === undefined) {
        const depMap = new Map<string, ChildDep[]>();

        for (let i = 0; i < questions.length; i++) {
          const q = questions[i];
          const parentField = q?.parent_question_field_name;
          const triggerValues = q?.parent_trigger_values;

          if (!parentField || !triggerValues || triggerValues.length === 0)
            continue;

          const children = depMap.get(parentField) || [];
          children.push({
            childIndex: i,
            triggerValues: triggerValues.filter((v): v is string => !!v),
            originalRequired: !!q?.required,
          });
          depMap.set(parentField, children);
        }

        depsRef.current = depMap;

        // Initial evaluation for pre-filled forms
        for (const [parentFieldName, children] of depMap) {
          const parentIndex = questions.findIndex(
            (q) => q?.field_name === parentFieldName,
          );
          if (parentIndex === -1) continue;

          const parentResponse = questions[parentIndex]?.response;

          for (const child of children) {
            const shouldBeRequired = doesResponseMatch(
              parentResponse as string | string[] | null | undefined,
              child.triggerValues,
            );
            const newRequired = shouldBeRequired ? true : child.originalRequired;
            if (questions[child.childIndex]?.required !== newRequired) {
              setValue(`questions.${child.childIndex}.required`, newRequired);
            }
          }
        }

        return;
      }

      // Handle individual parent response changes
      if (
        !name.startsWith("questions.") ||
        !name.endsWith(".response")
      ) {
        return;
      }

      const match = name.match(/questions\.(\d+)\.response/);
      if (!match) return;

      const changedIndex = Number.parseInt(match[1], 10);
      const changedQuestion = questions[changedIndex];
      if (!changedQuestion) return;

      const parentFieldName = changedQuestion.field_name;
      const children = depsRef.current.get(parentFieldName as string);
      if (!children) return;

      const parentResponse = changedQuestion.response;

      for (const child of children) {
        const shouldBeRequired = doesResponseMatch(
          parentResponse as string | string[] | null | undefined,
          child.triggerValues,
        );
        const newRequired = shouldBeRequired ? true : child.originalRequired;
        setValue(`questions.${child.childIndex}.required`, newRequired);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);
}

function doesResponseMatch(
  response: string | string[] | null | undefined,
  triggerValues: string[],
): boolean {
  if (response == null) return false;

  if (Array.isArray(response)) {
    return response.some((r) => triggerValues.includes(r));
  }

  return triggerValues.includes(response);
}
